import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { EditOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import Button from 'zignal-landscape/lib/Button';
import Banner from 'zignal-landscape/lib/Banner';
import Textarea from 'zignal-landscape/lib/Textarea';
import apiClient from '../../helpers/apiClient';
import { findBestMatchingPreset } from '../../helpers/jaccardSimilarity';
import PresetIntentions, { PRESET_INTENTIONS } from './PresetIntentions';
import styles from './IntentionFlow.css';

// Note: Custom intentions have been DISABLED in favor of presets (for now)
// To clean up, preset selection can to be extracted out of the stepper flow,
// but leaving existing structure for now (12/12/25) because it seems likely
// to be added back.

const DEFAULT_DASHBOARD_WIDGETS = [];

const IntentionFlow = ({
  onSave,
  onCancel,
  existingIntention = '',
  isEditing = false,
  dashboardTitle = '',
  dashboardDescription = '',
  dashboardId = '',
  activeProfileId = null,
  dashboardWidgets = DEFAULT_DASHBOARD_WIDGETS
}) => {
  // Extract text from intention object if needed
  const getIntentionText = (intention) => {
    if (!intention) {
      return '';
    }

    if (typeof intention === 'string') {
      return intention;
    }

    if (intention.text) {
      return intention.text;
    }

    return '';
  };

  const initialIntentionText = getIntentionText(existingIntention);

  // Calculate auto-selected preset based on dashboard widgets
  // CHANGED: Enable auto-selection for all users since everyone now uses presets
  const autoSelectedPreset = useMemo(() => {
    if (!dashboardWidgets || dashboardWidgets.length === 0) {
      return null;
    }

    // Create preset mappings for Jaccard similarity calculation
    const presetMappings = Object.entries(PRESET_INTENTIONS).reduce(
      (acc, [id, preset]) => {
        acc[id] = { widgetTypes: preset.widgetTypes };
        return acc;
      },
      {}
    );

    return findBestMatchingPreset(dashboardWidgets, presetMappings, 0.1);
  }, [dashboardWidgets]);

  // CHANGED: All users start at step -1 (preset selection) since custom intentions are disabled
  const initialStep = -1;
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [userIntention, setUserIntention] = useState(initialIntentionText);
  const [llmFeedback, setLlmFeedback] = useState('');
  const [clarificationNeeded, setClarificationNeeded] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [clarifyingQuestions, setClarifyingQuestions] = useState([]);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [isUsingPreset, setIsUsingPreset] = useState(false);
  const [savingPresetId, setSavingPresetId] = useState(null);

  useEffect(() => {
    setUserIntention(getIntentionText(existingIntention));
  }, [existingIntention]);

  const getDashboardContext = useCallback(() => {
    return (
      dashboardTitle ||
      dashboardDescription ||
      'social media monitoring and analysis'
    );
  }, [dashboardTitle, dashboardDescription]);

  const handleIntentionChange = useCallback(e => {
    setUserIntention(e?.target?.value || '');
  }, []);

  const handlePresetSelect = useCallback((preset) => {
    setSelectedPreset(preset);
    setUserIntention(preset.intention);
    setIsUsingPreset(true);
    setError(null);
    setSavingPresetId(preset.id);
    // CHANGED: Immediately save preset intention without going to step 0, skipping the custom intention field
    if (onSave) {
      onSave(preset.intention, preset.id);
    }
  }, [onSave]);

  const handleCustomClick = useCallback(() => {
    console.warn('Custom intentions are disabled - only presets are allowed');
    return;
  }, []);

  const processIntentionWithLLM = useCallback(async () => {
    try {
      const dashboardContext = getDashboardContext();

      const isInitialAnalysis = currentStep <= 1;

      if (isInitialAnalysis) {
        const response = await apiClient(
          '/api/ai/dashboards/intention/analyze',
          {
            method: 'POST',
            body: JSON.stringify({
              intention: userIntention,
              dashboardContext,
              profileIds: activeProfileId ? [activeProfileId] : []
            })
          }
        );

        if (!response.success) {
          throw new Error(response.error || 'Analysis failed');
        }

        const {
          feedback,
          needsClarification,
          clarifyingQuestions: questions = []
        } = response.analysis;

        setIsProcessing(false);
        setClarificationNeeded(needsClarification);
        setLlmFeedback(feedback);
        setClarifyingQuestions(questions);
        setCurrentStep(1);
        setError(null);
      } else {
        const response = await apiClient(
          '/api/ai/dashboards/intention/clarify',
          {
            method: 'POST',
            body: JSON.stringify({
              originalIntention: llmFeedback,
              clarifiedIntention: userIntention,
              dashboardContext,
              profileIds: activeProfileId ? [activeProfileId] : []
            })
          }
        );

        if (!response.success) {
          throw new Error(response.error || 'Clarification failed');
        }

        const { feedback, approved, finalSummary } = response.clarification;

        setIsProcessing(false);
        setClarificationNeeded(!approved);
        setLlmFeedback(finalSummary || feedback);
        setClarifyingQuestions([]);
        setCurrentStep(1);
        setError(null);
      }
    } catch (error) {
      console.error('AI intention analysis failed:', error);

      setIsProcessing(false);
      setError(
        error.message || 'Unable to analyze intention. Please try again.'
      );
      setClarificationNeeded(false);
      setLlmFeedback('');
      setCurrentStep(0);
    }
  }, [currentStep, userIntention, llmFeedback, getDashboardContext, activeProfileId]);

  const handleNext = useCallback(async () => {
    try {
      if (currentStep === -1) {
        // Preset selection step - should not reach here as presets handle their own navigation
        return;
      } else if (currentStep === 0) {
        if (!userIntention || (userIntention || '').trim().length < 10) {
          setError(
            'Please provide a more detailed intention (at least 10 characters)'
          );
          return;
        }

        // If using preset, skip LLM analysis
        if (isUsingPreset && selectedPreset) {
          handleSave();
          return;
        }

        // Step 1: Submit intention to LLM for review
        setIsProcessing(true);
        setCurrentStep(1);

        // Process intention with LLM
        await processIntentionWithLLM();
      } else if (currentStep === 1) {
        // Step 2: Handle LLM feedback
        if (clarificationNeeded) {
          // Don't automatically advance - let user choose to clarify or proceed
          return;
        }
        handleSave();
      } else if (currentStep === 2) {
        // Step 3: Re-submit clarified intention
        setIsProcessing(true);
        await processIntentionWithLLM();
      }
    } catch (error) {
      console.error('Error in handleNext:', error);
      setError('An unexpected error occurred. Please try again.');
      setIsProcessing(false);
    }
  }, [
    currentStep,
    clarificationNeeded,
    processIntentionWithLLM,
    userIntention,
    isUsingPreset,
    selectedPreset
  ]);

  const handleEdit = useCallback(() => {
    setCurrentStep(0);
    setError(null);
  }, []);

  const handleClarify = useCallback(() => {
    setCurrentStep(2);
  }, []);

  const handleSave = useCallback(() => {
    try {
      if (onSave && userIntention) {
        onSave(userIntention);
      }
    } catch (error) {
      console.error('Error saving intention:', error);
      setError('Failed to save intention. Please try again.');
    }
  }, [onSave, userIntention]);

  const handleCancel = useCallback(() => {
    try {
      if (onCancel) {
        onCancel();
      }
    } catch (error) {
      console.error('Error cancelling intention flow:', error);
    }
  }, [onCancel]);

  const renderStepContent = () => {
    switch (currentStep) {
      case -1:
        // Preset selection step (all users - custom intentions disabled)
        return (
          <div className={styles.stepContent}>
            <h4 className={styles.stepTitle}>
              Choose your dashboard focus
            </h4>
            <p className={styles.stepDescription}>
              Select a preset intention based on the template widgets.
              Your selection will determine how the AI analyzes your dashboard data.
            </p>
            <PresetIntentions
              onSelectPreset={handlePresetSelect}
              onCustomClick={handleCustomClick}
              hasExistingIntention={!!initialIntentionText}
              autoSelectedPresetId={autoSelectedPreset?.presetId}
              similarityScore={autoSelectedPreset?.similarityScore}
              savingPresetId={savingPresetId}
            />
          </div>
        );

      case 0:
        // DISABLED: Custom intentions - this step should no longer be reached
        // Presets now save immediately without going to step 0
        return (
          <div className={styles.stepContent}>
            <Banner type="info">
              <strong>Custom Intentions Disabled</strong>
              <p>
                Please select a preset intention from the previous step.
              </p>
            </Banner>
          </div>
        );

      case 1:
        if (isProcessing) {
          return (
            <div className={styles.stepContent}>
              <h4 className={styles.stepTitle}>
                Analyzing your intention...
              </h4>
              <div className={styles.processingIndicator}>
                <Banner type="info">
                  Analyzing your intention and checking for clarity...
                </Banner>
              </div>
            </div>
          );
        }

        return (
          <div className={styles.stepContent}>
            <h4 className={styles.stepTitle}>
              Review &amp; Confirmation
            </h4>
            <div className={styles.intentionReview}>
              <strong>Your intention:</strong>
              <div className={styles.userIntentionDisplay}>
                {userIntention || ''}
                <button
                  type="button"
                  onClick={handleEdit}
                  className={styles.editButton}
                  title="Edit intention"
                >
                  <EditOutlined />
                </button>
              </div>
            </div>
            <Banner
              type={clarificationNeeded ? 'warning' : 'success'}
              className={styles.llmFeedback}
            >
              <strong>AI Feedback</strong>
              <p>{llmFeedback}</p>
            </Banner>
            {clarificationNeeded && clarifyingQuestions.length > 0 && (
              <div className={styles.clarifyingQuestions}>
                <p className={styles.secondaryText}>
                  Consider these questions:
                </p>
                <ul>
                  {clarifyingQuestions.map((question, index) => (
                    <li key={index}>{question}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        );

      case 2:
        return (
          <div className={styles.stepContent}>
            <h4 className={styles.stepTitle}>
              Please provide clarification
            </h4>
            <Banner type="warning" className={styles.clarificationAlert}>
              <strong>Clarification Needed</strong>
              <p>{llmFeedback}</p>
            </Banner>
            {clarifyingQuestions.length > 0 && (
              <div className={styles.clarifyingQuestions}>
                <p className={styles.secondaryText}>
                  Consider these questions:
                </p>
                <ul>
                  {clarifyingQuestions.map((question, index) => (
                    <li key={index}>{question}</li>
                  ))}
                </ul>
              </div>
            )}
            <Textarea
              value={userIntention}
              onChange={handleIntentionChange}
              placeholder="Please provide more specific details based on the feedback above..."
              rows={4}
              className={styles.intentionInput}
            />
          </div>
        );

      default:
        return null;
    }
  };

  const canProceed =
    currentStep === 0 ? (userIntention || '').trim().length > 10 : true;
  const isLastStep = currentStep === 1 && !clarificationNeeded;

  return (
    <div className={styles.intentionFlow}>
      <div className={styles.contentSection}>
        {error && (
          <Banner type="warning" className={styles.errorAlert}>
            <strong>Connection Error</strong>
            <p>Unable to connect to AI service: {error}. Using fallback mode.</p>
          </Banner>
        )}
        {renderStepContent()}
      </div>

      <div className={styles.actionButtons}>
        <Button onClick={handleCancel} className={styles.cancelButton}>
          Cancel
        </Button>
        {currentStep === -1 ? null : currentStep === 1 && clarificationNeeded ? (
          <div>
            <Button onClick={handleSave} className={styles.proceedButton}>
              Proceed Anyway
            </Button>
            <Button
              primary
              onClick={handleClarify}
              className={styles.clarifyButton}
            >
              Provide More Details
            </Button>
          </div>
        ) : (
          <Button
            primary
            onClick={handleNext}
            disabled={!canProceed || isProcessing}
            className={styles.nextButton}
          >
            {isProcessing ? 'Processing...' : isLastStep ? 'Save & Continue' : 'Next'}
          </Button>
        )}
      </div>
    </div>
  );
};

IntentionFlow.propTypes = {
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  existingIntention: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  isEditing: PropTypes.bool,
  dashboardTitle: PropTypes.string,
  dashboardDescription: PropTypes.string,
  dashboardId: PropTypes.string,
  activeProfileId: PropTypes.string,
  dashboardWidgets: PropTypes.array
};

export default IntentionFlow;
